import { PDFDocument, degrees } from 'pdf-lib';
import { renderPdfPageToImage } from './ANNPDFPageToImage';

/** Extra clockwise rotation in degrees per 1-based source page, on top of the page's own /Rotate. */
export type PdfPageRotations = Readonly<Record<number, number>>;

export async function cutPdfToSelectedPages(
   pdfBlob: Blob,
   pagesToKeep: number[],
   rotations: PdfPageRotations = {},
): Promise<Blob> {
   if (pagesToKeep.length === 0) {
      throw new Error("No pages selected to keep");
   }

   const uniquePages = [...new Set(pagesToKeep)].sort((a, b) => a - b);

   try {
      return await cutPdfViaPdfLib(pdfBlob, uniquePages, rotations);
   } catch (err: any) {
      // Some PDFs have a catalog/page-tree structure pdf-lib v1.17.1 can't parse
      // (typical symptom: "catalog.Pages(...).traverse is not a function").
      // Fall back to rasterizing each selected page via pdfjs and re-assembling
      // a fresh PDF. This loses vector content but always works for PDFs pdfjs
      // can render.
      const message = typeof err?.message === "string" ? err.message : String(err);
      const shouldFallback =
         /traverse is not a function/i.test(message) ||
         /Pages\(\)/i.test(message) ||
         /PDFPageTree/i.test(message) ||
         /Failed to parse/i.test(message);
      if (!shouldFallback) {
         throw err;
      }
      return await cutPdfViaRasterization(pdfBlob, uniquePages, rotations);
   }
}

async function cutPdfViaPdfLib(pdfBlob: Blob, uniquePages: number[], rotations: PdfPageRotations): Promise<Blob> {
   const arrayBuffer = await pdfBlob.arrayBuffer();
   const pdfDoc = await PDFDocument.load(arrayBuffer);
   const totalPages = pdfDoc.getPageCount();

   const invalidPages = uniquePages.filter(p => p < 1 || p > totalPages);
   if (invalidPages.length > 0) {
      throw new Error(`Invalid page numbers: ${invalidPages.join(', ')}. PDF has ${totalPages} pages.`);
   }

   const newPdf = await PDFDocument.create();

   for (const pageNum of uniquePages) {
      const [copiedPage] = await newPdf.copyPages(pdfDoc, [pageNum - 1]);
      const extraRotation = rotations[pageNum] ?? 0;
      if (extraRotation % 360 !== 0) {
         const angle = copiedPage.getRotation().angle + extraRotation;
         copiedPage.setRotation(degrees(((angle % 360) + 360) % 360));
      }
      newPdf.addPage(copiedPage);
   }

   const pdfBytes = await newPdf.save();
   const blobBytes = new Uint8Array(pdfBytes);
   return new Blob([blobBytes], { type: 'application/pdf' });
}

async function cutPdfViaRasterization(pdfBlob: Blob, uniquePages: number[], rotations: PdfPageRotations): Promise<Blob> {
   const newPdf = await PDFDocument.create();

   for (const pageNum of uniquePages) {
      const pngBlob = await renderPdfPageToImage(pdfBlob, pageNum, 2.0, rotations[pageNum] ?? 0);
      const pngBytes = await pngBlob.arrayBuffer();
      const pngImage = await newPdf.embedPng(pngBytes);
      const page = newPdf.addPage([pngImage.width, pngImage.height]);
      page.drawImage(pngImage, {
         x: 0,
         y: 0,
         width: pngImage.width,
         height: pngImage.height,
      });
   }

   const pdfBytes = await newPdf.save();
   const blobBytes = new Uint8Array(pdfBytes);
   return new Blob([blobBytes], { type: 'application/pdf' });
}

export async function getPdfPageCount(pdfBlob: Blob): Promise<number> {
   const arrayBuffer = await pdfBlob.arrayBuffer();
   const pdfDoc = await PDFDocument.load(arrayBuffer);
   return pdfDoc.getPageCount();
}
